import React, { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthConfig } from '../contexts/AuthConfigContext';

const ProtectedRoute = ({ children, onLoginRequired, allowGuest }) => {
  const { isLoggedIn } = useAuth();
  const { isGuestCheckoutEnabled, loading: authConfigLoading } = useAuthConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const hasTriggeredRef = useRef(false);

  const isAllowed = isLoggedIn || (allowGuest && isGuestCheckoutEnabled());

  // Trigger login modal + redirect in useLayoutEffect (before paint)
  // to avoid the old pattern of calling setState during render
  useLayoutEffect(() => {
    // Wait until the auth config (which carries guest-checkout enablement) has
    // loaded. On a hard refresh it starts as "not enabled", which would wrongly
    // bounce a guest to the login modal before site details arrive.
    if (allowGuest && authConfigLoading) return;
    if (!isAllowed && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      if (onLoginRequired) {
        onLoginRequired();
      }
      navigate('/', { replace: true, state: { from: location } });
    }
  }, [isAllowed, authConfigLoading, allowGuest, onLoginRequired, navigate, location]);

  // Reset when user becomes allowed (e.g. after login)
  useLayoutEffect(() => {
    if (isAllowed) {
      hasTriggeredRef.current = false;
    }
  }, [isAllowed]);

  if (isAllowed) {
    return children;
  }

  // While the auth config is still loading for a guest-allowed route, render
  // nothing (no redirect/modal) — the decision is deferred until config arrives.
  return null;
};

export default ProtectedRoute;
